import { type Dispatch, useEffect } from 'react';

import { isSafeAssetUrl } from '@/helpers/assetUrl';
import { type ResolvedBranding } from '@/schemas/cashierSession.schema';
import { type Action } from '@/state';
import { applyBranding } from '@/theme/applyBranding';
import { type Scheme } from '@/theme/defaults';
import { resolveScheme, subscribeToSchemeChanges } from '@/theme/resolveScheme';
import { resolveTokens } from '@/theme/tokens';

function applyFrom(branding: ResolvedBranding, scheme: Scheme): void {
  const assets = scheme === 'dark' ? branding.dark : branding.light;
  const { vars, notes } = resolveTokens(assets.theme, scheme);
  applyBranding({
    vars,
    scheme,
    notes,
    faviconUrl: isSafeAssetUrl(assets.faviconUrl) ? assets.faviconUrl : null,
  });
}

/**
 * Applies merchant branding once per session, then again only when `colorScheme: 'system'` and the
 * OS scheme changes. A poll must never re-apply — that flash reads as a broken payment page.
 */
export function useApplyBranding(
  branding: ResolvedBranding | null,
  brandingApplied: boolean,
  dispatch: Dispatch<Action>,
): void {
  useEffect(() => {
    if (branding === null || brandingApplied) return;
    const scheme = resolveScheme(branding.colorScheme);
    applyFrom(branding, scheme);
    dispatch({ type: 'BRANDING_APPLIED', scheme });
  }, [branding, brandingApplied, dispatch]);

  useEffect(() => {
    if (branding === null) return;
    return subscribeToSchemeChanges(branding.colorScheme, (scheme) => {
      applyFrom(branding, scheme);
      dispatch({ type: 'SCHEME_SET', scheme });
    });
  }, [branding, dispatch]);
}
